/**
 * The candidate session — PERSISTED to localStorage, deliberately.
 *
 * This is the opposite choice from the admin console, and the difference is intentional:
 *
 *   - The admin rail keeps its token IN MEMORY (a reload signs you out) because it is a scripted
 *     staff console that can reveal a referee's private words — there, reload-signs-out is the
 *     correct, conservative trade.
 *   - candidate-web is a CONSUMER app. A candidate expects to stay signed in across a reload and a
 *     return visit, exactly like any other app they use; being logged out every refresh would be a
 *     bug, not a feature. So the rotating token pair is persisted.
 *
 * What makes that acceptable here (and not on the admin rail):
 *   - The refresh token ROTATES and is single-use: replaying a spent one revokes the whole family
 *     server-side, so a stale copy is worthless.
 *   - The data behind a candidate token is the candidate's OWN — their requests and their referees'
 *     contact details — not a third party's private recording. The blast radius is the account
 *     holder's, and they are the one choosing to stay signed in.
 *   - The origin's CSP is `script-src 'self'` with no third-party scripts (public/_headers), which
 *     is the mitigation that makes localStorage tolerable: the realistic localStorage threat is an
 *     injected foreign script, and there is no seam for one.
 *
 * This store is the ONLY writer of the token. `currentAccessToken()` is what the http client's
 * per-request provider calls, so a rotated token is picked up without rebuilding the client.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { TokenResponse } from '@/lib/wire';

export interface CandidateSessionState {
  accessToken: string | null;
  /** ISO-8601. Drives proactive rotation; see `tokenProvider` in lib/api-client.http.ts. */
  accessTokenExpiresAt: string | null;
  refreshToken: string | null;
  refreshTokenExpiresAt: string | null;
  /** Present from /otp/verify; the /refresh response does not re-state it (userId: null). */
  userId: string | null;
  signIn: (tokens: TokenResponse) => void;
  /** Replace the pair after a rotation, preserving the userId the refresh response omits. */
  rotate: (tokens: TokenResponse) => void;
  signOut: () => void;
}

const SIGNED_OUT = {
  accessToken: null,
  accessTokenExpiresAt: null,
  refreshToken: null,
  refreshTokenExpiresAt: null,
  userId: null,
} as const;

export const useCandidateSession = create<CandidateSessionState>()(
  persist(
    (set) => ({
      ...SIGNED_OUT,
      signIn: (tokens) =>
        set({
          accessToken: tokens.accessToken,
          accessTokenExpiresAt: tokens.accessTokenExpiresAt,
          refreshToken: tokens.refreshToken,
          refreshTokenExpiresAt: tokens.refreshTokenExpiresAt,
          userId: tokens.userId,
        }),
      rotate: (tokens) =>
        set((state) => ({
          accessToken: tokens.accessToken,
          accessTokenExpiresAt: tokens.accessTokenExpiresAt,
          refreshToken: tokens.refreshToken,
          refreshTokenExpiresAt: tokens.refreshTokenExpiresAt,
          // /refresh returns userId: null by design — it rotates the pair, it does not re-state
          // identity. Keep what sign-in established rather than blanking it.
          userId: tokens.userId ?? state.userId,
        })),
      signOut: () => set({ ...SIGNED_OUT }),
    }),
    {
      name: 'bmw-candidate-session',
      // Only the token fields are persisted — never the action functions.
      partialize: (state) => ({
        accessToken: state.accessToken,
        accessTokenExpiresAt: state.accessTokenExpiresAt,
        refreshToken: state.refreshToken,
        refreshTokenExpiresAt: state.refreshTokenExpiresAt,
        userId: state.userId,
      }),
    },
  ),
);

/** Read the current access token without subscribing (for the client's per-request provider). */
export function currentAccessToken(): string | null {
  return useCandidateSession.getState().accessToken;
}

/** True when a candidate token is held. The single predicate the auth guard reads. */
export function isSignedIn(): boolean {
  return currentAccessToken() !== null;
}

/** Drop the session. Called on sign-out and whenever a refresh fails (the session is over). */
export function signOut(): void {
  useCandidateSession.getState().signOut();
}
